<script lang="ts" setup>
import LocationPop from './LocationPop.vue'
import type { ILocation } from '@/utils/types'
const props = defineProps({
  lists: {
    type: Array<ILocation>,
    default: []
  },
  current: {
    type: Number,
    default: null
  },
  filterQuery: {
    type: String,
    default: ''
  }
})

const router = useRouter()

const propsLists = computed(() => props.lists)
const filterString = computed(() => props.filterQuery)

const refLocationPop = ref()

const toLocationDetail = (key: string) => {
  router.push(`/locations/${key}`)
}

const handleOpenPop = (index: number) => {
  let el = document.querySelectorAll('.lists li')
  refLocationPop.value.openPop(el[index].getBoundingClientRect())

  listIsPopDisabled()
  propsLists.value[index]['isPop'] = true
}

// 先將所有isPop設為false
const listIsPopDisabled = () => {
  propsLists.value.forEach((item: any) => {
    item.isPop = false
  })
}

const handlePopEmitClose = () => {
  listIsPopDisabled()
  router.push(`/locations?${filterString.value}`)
}

onMounted(() => {
  if (props.current >= 0) {
    handleOpenPop(props.current)
  }
})
</script>

<template>
  <ul class="lists">
    <li v-for="(item, index) in propsLists" :key="item.id">
      <div class="lists__item" :class="{ isPop: item.isPop }">
        <nuxt-link class="lists__top" :to="`/locations/${item.seo.meta_slug || item.id}?${filterString}`">
          <div class="lists__top__item">
            <div class="lists__type">{{ item.class }}</div>
            <div class="lists__title">{{ item.subject }}</div>
            <div class="lists__top__city">{{ item.city }}</div>
          </div>
          <div class="lists__box">
            <div class="lists__city">{{ item.city }}</div>
            <figure class="lists__image">
              <Nimg :src="item.image?.link" :title="item.image?.title" :alt="item.image?.alt" format="webp" />
            </figure>
          </div>
        </nuxt-link>
        <div class="lists__bottom">
          <div class="lists__address">
            {{ item.zipcode ? `(${item.zipcode})` : '' }}
            {{
              `${item.region}
            ${item.city}${item.town}${item.address}`
            }}
          </div>
          <nuxt-link class="lists__view lists__icon"
            :to="`/locations/${item.seo.meta_slug || item.id}?${filterString}`">
            <svg-icon name="icon-store" />
          </nuxt-link>
          <nuxt-link :to="`https://www.google.com.tw/maps/place/${item.region}${item.city}${item.town}${item.address}`"
            class="lists__map lists__icon" target="_blank">
            <svg-icon name="icon-map" />
          </nuxt-link>
        </div>
      </div>
    </li>
  </ul>
  <LocationPop ref="refLocationPop" @update:close="handlePopEmitClose" :current="current" />
</template>

<style lang="scss" scoped>
.lists {
  display: flex;
  flex-wrap: wrap;
  margin-left: toRem(-1);
  margin-right: toRem(-1);

  li {
    width: 25%;
    padding-left: toRem(12);
    padding-right: toRem(12);
    margin-bottom: toRem(45);

    @include max-media(1280) {
      width: 33.333%;
    }

    @include max-media(960) {
      width: 50%;
    }

    @include max-media(640) {
      width: 100%;
    }
  }

  &__item {
    transition: all 0.5s ease-in-out;

    &.isPop {
      opacity: 0;
    }
  }

  &__top {
    display: flex;
    flex-direction: column;
    padding: toRem(20);
    min-height: toRem(375);
    background-color: $color-white-1;
    color: $color-white-1;
    cursor: pointer;
    transition: all 0.5s ease-in-out;

    @include max-media(480) {
      flex-direction: row;
      justify-content: space-between;
      min-height: auto;
    }

    &:hover {
      background-color: $color-gray-3;

      .lists {

        &__type,
        &__title,
        &__city {
          color: $color-white-1;
        }
      }
    }

    &__item {
      display: flex;
      flex-direction: column;
      padding-right: toRem(10);
    }

    &__city {
      margin-top: auto;
      font-size: toRem(20);
      font-weight: 500;

      @include min-media(481) {
        display: none;
      }

      @include max-media(480) {
        font-size: toRem(16);
      }
    }
  }

  &__city {
    font-size: toRem(20);
    font-weight: 500;
    line-height: 1.4;
    color: $color-black-1;
    transition: all 0.5s ease-in-out;

    @include max-media(480) {
      display: none;
    }
  }

  &__type {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: toRem(5);
    font-weight: 700;
    font-size: toRem(16);
    line-height: 1.6;
    color: $color-gray-1;
    transition: all 0.5s ease-in-out;

    &::before {
      content: '#';
      display: flex;
      align-items: center;
      justify-content: center;
      width: toRem(24);
      height: toRem(24);
      margin-right: toRem(6);
      border-radius: 50%;
      font-size: toRem(14);
      font-weight: 700;
      color: $color-white-1;
      background-color: $color-brand-3;

      @include max-media(640) {
        background-color: $color-brand-1;
      }
    }

    @include max-media(480) {
      font-size: toRem(14);
    }
  }

  &__title {
    font-size: toRem(22);
    font-weight: 500;
    color: $color-black-1;
    transition: all 0.5s ease-in-out;

    @include max-media(480) {
      font-size: toRem(16);
    }
  }

  &__box {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: auto;
  }

  &__image {
    position: relative;
    width: toRem(160);
    height: toRem(160);

    @include max-media(420) {
      width: toRem(120);
      height: toRem(120);
    }

    @include max-media(360) {
      width: toRem(100);
      height: toRem(100);
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__bottom {
    display: flex;
    flex-wrap: wrap;
  }

  &__address {
    width: 100%;
    font-size: toRem(16);
    padding-top: toRem(12);
    padding-bottom: toRem(12);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: toRem(48);
    height: toRem(48);
    border-radius: 50%;
    border: 1px solid $color-gray-line;

    svg {
      width: toRem(24);
      height: toRem(24);
    }
  }

  &__view,
  &__map {
    font-size: toRem(16);
    font-weight: 700;
    color: $color-gray-1;
  }

  &__view {
    cursor: pointer;
    margin-right: toRem(12);
  }
}
</style>
